import { isNil, isEmpty as _isEmpty } from 'lodash-es';

function isEmpty(value) {
  return value === '' || isNil(value);
}

function validateDefault(value) {
  return value !== undefined && value !== '' && value !== null && value !== false;
}

function validateEmail(email) {
  // eslint-disable-next-line no-useless-escape
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

function validatePhone(phone) {
  return !isEmpty(phone) && phone.length >= 10;
}

function validateName(name) {
  return name && name.length > 0;
}

function validateCompanyName(name) {
  return name && name.trim().length > 0;
}

function validateIbanFormat(cleanIban) {
  const basicFormatRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;
  if (!basicFormatRegex.test(cleanIban)) {
    return { isValid: false, error: 'Format IBAN invalide' };
  }

  if (cleanIban.length < 15) {
    return { isValid: false, error: 'IBAN trop court' };
  }

  if (cleanIban.length > 34) {
    return { isValid: false, error: 'IBAN trop long' };
  }

  return { isValid: true, error: null };
}

function validateIbanLength(cleanIban) {
  const countryLengths = {
    'FR': 27,
    'GB': 22,
    'CA': 24,
    'US': 34
  };

  const countryCode = cleanIban.substring(0, 2);
  const expectedLength = countryLengths[countryCode];

  if (expectedLength && cleanIban.length !== expectedLength) {
    return { isValid: false, error: `IBAN ${countryCode} doit contenir ${expectedLength} caractères` };
  }

  return { isValid: true, error: null };
}

function validateIbanChecksum(cleanIban) {
  const rearranged = cleanIban.substring(4) + cleanIban.substring(0, 4);

  let numericString = '';
  for (let i = 0; i < rearranged.length; i++) {
    const char = rearranged[i];
    if (char >= 'A' && char <= 'Z') {
      numericString += (char.charCodeAt(0) - 55).toString();
    } else {
      numericString += char;
    }
  }

  let remainder = '';
  for (let i = 0; i < numericString.length; i++) {
    remainder += numericString[i];
    if (remainder.length >= 9) {
      remainder = (parseInt(remainder, 10) % 97).toString();
    }
  }

  const checksum = parseInt(remainder, 10) % 97;
  return checksum === 1;
}

function validateIban(iban) {
  if (!iban) {
    return { isValid: false, error: 'IBAN requis' };
  }

  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  const formatCheck = validateIbanFormat(cleanIban);
  if (!formatCheck.isValid) return formatCheck;

  const lengthCheck = validateIbanLength(cleanIban);
  if (!lengthCheck.isValid) return lengthCheck;

  const checksumValid = validateIbanChecksum(cleanIban);
  if (!checksumValid) {
    return { isValid: false, error: 'IBAN invalide - vérification échouée' };
  }

  return { isValid: true, error: null };
}

function formatIban(iban) {
  if (!iban) return '';

  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  const alphanumericOnly = cleanIban.replace(/[^A-Z0-9]/g, '');

  return alphanumericOnly.replace(/(.{4})/g, '$1 ').trim();
}

function validateContact(contact) {
  return validateName(contact.firstName)
    && validateName(contact.lastName)
    && validateName(contact.phoneNumber)
    && !isEmpty(contact.position);
}

function validateEditContact(contact) {
  return validateName(contact.firstName)
    && validateName(contact.lastName)
    && validateName(contact.phoneNumber);
}

function validateRevenue(revenue) {
  return parseInt(revenue, 10) > 0;
}

function validateSiret(siret) {
  return !Number.isNaN(Number(siret)) && siret.toString().length === 14;
}

function validateOrias(orias) {
  return !Number.isNaN(Number(orias)) && orias.toString().length === 8;
}

function validatePassword(password, confirmPassword = null) {
  const validateIdentical = (
    password === null
    || confirmPassword === null
    || password.length === 0
    || confirmPassword.length === 0)
    ? null : password === confirmPassword;
  return [
    {
      is: 'lowercase',
      isValid: (/[a-z]/g).test(password),
      message: '1 caractère minuscule',
    },
    {
      is: 'number',
      isValid: (/[0-9]/g).test(password),
      message: '1 chiffre',
    },
    {
      is: 'identical',
      isValid: validateIdentical,
      message: 'Mots de passe identiques',
    },
    {
      is: 'uppercase',
      isValid: (/[A-Z]/g).test(password),
      message: '1 caractère majuscule',
    },
    {
      is: 'lettering',
      isValid: password && password.length > 7,
      message: '8 caractères minimum',
    }].map((elem) => (password === null || password === '' ? Object.assign(elem, { isValid: null }) : elem));
}

function validateCci(number) {
  const cciRegex = new RegExp(/^\w{1,}$/);
  return cciRegex.test(number);
}

function validateAddress(address) {
  if (_isEmpty(address)
    || ((address.city === '')
    && (address.country === '')
    && (address.street === '')
    && (address.postalCode === ''))
  ) {
    return 0;
  }
  if (!address
    || isEmpty(address.city)
    || isEmpty(address.country)
    || isEmpty(address.street)
    || isEmpty(address.postalCode)) {
    return false;
  }
  return true;
}

function validateCompany(company) {
  return validateAddress(company.address)
    && (validateSiret(company.siret) || company.inCreation)
    && company.name.trim().length > 0
    && !isEmpty(company.formeJuridique)
    && !isEmpty(company.codeNaf);
}

function validateNewCompany(company) {
  return validateAddress(company.address)
    && company.name.trim().length > 0;
}

function validateProfessionalCard(card) {
  if (!card || isEmpty(card)) return false;
  return !isEmpty(card.deliveryCity)
    && !isEmpty(card.deliveryDate)
    && !isEmpty(card.approverFirstName)
    && !isEmpty(card.approverLastName)
    && card.cardNumber
    && card.cardNumber.length > 0
    && card.activities
    && card.activities.length > 0
    && (card.activities.includes('GESTION_LOCATIVE')
    || card.activities.includes('TRANSACTION_IMMO'));
}

function validateWarrant(warrant) {
  return validateProfessionalCard(warrant.professionalCard)
    && validateSiret(warrant.siret)
    && !isEmpty(warrant.name)
    && !isEmpty(warrant.registrationCity)
    && !isEmpty(warrant.insurerName)
    && !isEmpty(warrant.contractNumber)
    && !isEmpty(warrant.contractEffectiveDate);
}

function validateRepresentative(representative) {
  return validateAddress(representative.address)
    && !isEmpty(representative.firstName)
    && !isEmpty(representative.lastName);
}

function validateSubsidiary(subsidiary) {
  return validateAddress(subsidiary.address)
  && validateSiret(subsidiary.siret)
  && validateOrias(subsidiary.oriasNumber)
  && !isEmpty(subsidiary.name);
}

function validateMrpLocal(local) {
  const isNumber = (area) => !Number.isNaN(Number(area));
  return validateAddress(local.address)
  && isNumber(local.area)
  && local.area <= 1000
  && local.area > 0
  && !isEmpty(local.occupant);
}

function validateMrpContent(content) {
  return parseInt(content, 10) <= 1000000 && parseInt(content, 10) > 0;
}

function validateFixContent(fixContent) {
  return parseInt(fixContent, 10) <= 500000 && parseInt(fixContent, 10) > 0;
}

function validateMobileContent(mobileContent) {
  return parseInt(mobileContent, 10) <= 275000 && parseInt(mobileContent, 10) > 0;
}

function validateCallCenter(fields) {
  return validatePhone(fields.phone)
    && validateName(fields.name)
    && validateName(fields.company)
    && !isEmpty(fields.time.time)
    && !isEmpty(fields.time.date);
}

function validateDownloadForm(contact) {
  return validatePhone(contact.phoneNumber)
  && validateName(contact.firstName)
  && validateName(contact.lastName)
  && validateEmail(contact.email);
}

function validateMonthlyPayment(account) {
  return !isEmpty(account.iban) && !isEmpty(account.accountHolderName);
}

function validateList(list, labels) {
  if (list.length === 0) {
    return false;
  }
  for (let index = 0; index < list.length; index += 1) {
    const element = list[index];
    for (let iter = 0; iter < labels.length; iter += 1) {
      const label = labels[iter];
      if (element[label.key] === '') {
        return false;
      }
    }
    const address = element;
    if (!address
      || isEmpty(address.country)
      || isEmpty(address.city)
      || isEmpty(address.street)
      || isEmpty(address.postalCode)) {
      return false;
    }
  }
  return true;
}

export {
  isEmpty,
  validateDefault,
  validateAddress,
  validateCallCenter,
  validateCci,
  validateContact,
  validateCompany,
  validateDownloadForm,
  validateEditContact,
  validateEmail,
  validateIban,
  formatIban,
  validateList,
  validateMrpLocal,
  validateMrpContent,
  validateOrias,
  validatePhone,
  validatePassword,
  validateProfessionalCard,
  validateRevenue,
  validateSiret,
  validateSubsidiary,
  validateWarrant,
  validateRepresentative,
  validateMonthlyPayment,
  validateFixContent,
  validateMobileContent,
  validateNewCompany,
  validateCompanyName,
};
